import { format, formatDistanceToNow } from "date-fns";
import {
  AlertTriangle,
  ArrowRight,
  BookOpen,
  CheckCircle,
  Clock,
  FileText,
  Lock,
  Sparkles,
  Users,
} from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AutoAssignModal } from "@/components/admin/AutoAssignModal";
import { StatusBadge } from "@/components/admin/StatusBadge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ErrorState, LoadingState } from "@/components/ui/data-states";
import { useAdminScope } from "@/contexts/AdminScopeContext";
import { type AdminDashboardData, useAdminDashboard } from "@/hooks/admin/dashboard";
import { type AdminProgrammeItem, useAdminProgrammes } from "@/hooks/admin/programmes";
import { AdminLayout } from "@/layouts/AdminLayout";
import { cn } from "@/lib/utils";

type ProgrammeItem = AdminProgrammeItem;
type DashboardData = AdminDashboardData;

interface WindowInfo {
  assignment_id: string;
  week_number: number;
  assignment_number: number;
  start_date: string;
  deadline: string;
  time_status: string;
  assigned_count: number;
  total_enrolled: number;
  users_need_topics: number;
  submitted_count: number;
}

interface TimelineEntry {
  week_number: number;
  assignment_number: number;
  assignment_id: string;
  start_date: string;
  deadline: string;
  time_status: string;
  assigned_count: number;
  submitted_count: number;
  total_enrolled: number;
}

// Topic Inventory Card
function TopicInventoryCard({
  inventory,
  onManageTopics,
  onGenerateTopics,
}: {
  inventory: DashboardData["topic_inventory"];
  onManageTopics: () => void;
  onGenerateTopics: () => void;
}) {
  const hasShortfall = inventory.shortfall > 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg text-gray-900">Topic Inventory</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3 mb-4">
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Ready Topics</span>
            <div className="flex items-center gap-2">
              <span className="text-gray-900 font-semibold">{inventory.ready}</span>
              {hasShortfall && (
                <span className="flex items-center gap-1 text-amber-600 text-sm">
                  <AlertTriangle className="w-4 h-4" />
                  Low
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Draft Topics</span>
            <span className="text-gray-500">{inventory.draft}</span>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-4 mb-4">
          <div className="flex items-center justify-between mb-1">
            <span className="text-gray-600">Upcoming Need</span>
            <span className="text-gray-900">{inventory.projected_need}</span>
          </div>
          {hasShortfall && (
            <div className="flex items-center justify-between text-amber-600">
              <span>Shortfall</span>
              <span className="font-semibold">{inventory.shortfall}</span>
            </div>
          )}
        </div>

        <div className="flex gap-2">
          <button
            onClick={onGenerateTopics}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gray-900 hover:bg-gray-800 text-white rounded-lg transition-colors text-sm font-medium"
          >
            <Sparkles className="w-4 h-4" />
            Generate
          </button>
          <button
            onClick={onManageTopics}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors text-sm"
          >
            Manage
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

// Window Card Component
function WindowCard({
  window,
  label,
  isCurrent,
  onAutoAssign,
}: {
  window: WindowInfo | null;
  label: string;
  isCurrent: boolean;
  onAutoAssign?: () => void;
}) {
  if (!window) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-gray-400 text-center py-8">No {label.toLowerCase()} available</div>
        </CardContent>
      </Card>
    );
  }

  const getStatusIcon = () => {
    switch (window.time_status) {
      case "active":
        return <Clock className="w-5 h-5 text-gray-700" />;
      case "locked":
        return <Lock className="w-5 h-5 text-gray-400" />;
      case "closed":
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      default:
        return null;
    }
  };

  const getTimeLabel = () => {
    const deadline = new Date(window.deadline);
    const start = new Date(window.start_date);

    if (window.time_status === "active") {
      return `Ends ${formatDistanceToNow(deadline, { addSuffix: true })}`;
    }
    if (window.time_status === "locked") {
      return `Opens ${formatDistanceToNow(start, { addSuffix: true })}`;
    }
    return "Closed";
  };

  const needsTopics = window.users_need_topics > 0;
  const allAssigned = window.assigned_count === window.total_enrolled;

  return (
    <Card className={cn(isCurrent && "ring-2 ring-gray-900")}>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-gray-500 text-sm mb-1">{label}</div>
            <CardTitle className="text-lg text-gray-900">
              Week {window.week_number} - Assignment {window.assignment_number}
            </CardTitle>
          </div>
          {getStatusIcon()}
        </div>
        <div className="text-sm text-gray-500">{getTimeLabel()}</div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3 mb-4">
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Topics Assigned</span>
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  "font-semibold",
                  allAssigned ? "text-green-600" : needsTopics ? "text-amber-600" : "text-gray-900"
                )}
              >
                {window.assigned_count}/{window.total_enrolled}
              </span>
              {allAssigned && <CheckCircle className="w-4 h-4 text-green-500" />}
              {needsTopics && <AlertTriangle className="w-4 h-4 text-amber-500" />}
            </div>
          </div>

          {isCurrent && (
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Submitted</span>
              <span className="text-gray-900">
                {window.submitted_count}/{window.total_enrolled}
              </span>
            </div>
          )}

          {needsTopics && (
            <div className="text-amber-600 text-sm">
              {window.users_need_topics} user{window.users_need_topics !== 1 ? "s" : ""} need topics
            </div>
          )}
        </div>

        {needsTopics && onAutoAssign && (
          <button
            onClick={onAutoAssign}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-900 hover:bg-gray-800 text-white rounded-lg transition-colors font-medium"
          >
            <Sparkles className="w-4 h-4" />
            Auto-Assign {window.users_need_topics} Topic{window.users_need_topics !== 1 ? "s" : ""}
          </button>
        )}
      </CardContent>
    </Card>
  );
}

// Week Timeline Component
function WeekTimeline({
  timeline,
  totalEnrolled,
}: {
  timeline: TimelineEntry[];
  totalEnrolled: number;
}) {
  // Group by week
  const weeks: Record<number, TimelineEntry[]> = {};
  timeline.forEach((entry) => {
    if (!weeks[entry.week_number]) {
      weeks[entry.week_number] = [];
    }
    weeks[entry.week_number].push(entry);
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return (
          <StatusBadge tone="info" className="inline-flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Active
          </StatusBadge>
        );
      case "closed":
        return (
          <StatusBadge tone="neutral" className="inline-flex items-center gap-1">
            <CheckCircle className="w-3 h-3" />
            Closed
          </StatusBadge>
        );
      default:
        return (
          <StatusBadge tone="neutral" className="inline-flex items-center gap-1">
            <Lock className="w-3 h-3" />
            Locked
          </StatusBadge>
        );
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg text-gray-900">Programme Timeline</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {Object.entries(weeks).map(([weekNum, entries]) => (
            <div key={weekNum}>
              <div className="text-sm text-gray-500 mb-2 font-medium">Week {weekNum}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {entries
                  .sort((a, b) => a.assignment_number - b.assignment_number)
                  .map((entry) => {
                    const missedCount =
                      entry.time_status === "closed" ? totalEnrolled - entry.submitted_count : 0;
                    const isActive = entry.time_status === "active";
                    const isClosed = entry.time_status === "closed";
                    const isLocked = entry.time_status === "locked";

                    return (
                      <div
                        key={entry.assignment_id}
                        className={cn(
                          "rounded-lg p-4 transition-all",
                          isActive && "bg-white border-2 border-gray-900 shadow-md",
                          isClosed && "bg-white border border-gray-200",
                          isLocked && "bg-gray-50 border border-gray-200 opacity-75"
                        )}
                      >
                        {/* Header */}
                        <div className="flex items-center justify-between mb-2">
                          <span
                            className={cn(
                              "font-semibold",
                              isActive && "text-gray-900",
                              isClosed && "text-gray-900",
                              isLocked && "text-gray-500"
                            )}
                          >
                            Assignment {entry.assignment_number}
                          </span>
                          {getStatusBadge(entry.time_status)}
                        </div>

                        {/* Datetime */}
                        <div
                          className={cn(
                            "text-xs mb-3",
                            isLocked ? "text-gray-400" : "text-gray-500"
                          )}
                        >
                          {format(new Date(entry.start_date), "MMM d, HH:mm")} →{" "}
                          {format(new Date(entry.deadline), "MMM d, HH:mm")}
                        </div>

                        {/* Stats */}
                        {isClosed ? (
                          <div className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2">
                              <span className="text-gray-900 font-medium">
                                {entry.submitted_count}/{totalEnrolled}
                              </span>
                              <span className="text-gray-500">submitted</span>
                            </div>
                            {missedCount > 0 && (
                              <div className="flex items-center gap-1 text-amber-600">
                                <AlertTriangle className="w-3.5 h-3.5" />
                                <span>{missedCount} missed</span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {/* Assigned progress */}
                            <div>
                              <div className="flex items-center justify-between text-xs mb-1">
                                <span className={isLocked ? "text-gray-400" : "text-gray-500"}>
                                  Assigned
                                </span>
                                <span className={isLocked ? "text-gray-500" : "text-gray-900"}>
                                  {entry.assigned_count}/{totalEnrolled}
                                </span>
                              </div>
                              <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                  className={cn(
                                    "h-full rounded-full transition-all",
                                    isActive ? "bg-gray-900" : "bg-gray-400"
                                  )}
                                  style={{
                                    width: `${totalEnrolled > 0 ? (entry.assigned_count / totalEnrolled) * 100 : 0}%`,
                                  }}
                                />
                              </div>
                            </div>
                            {/* Submitted progress (only for active) */}
                            {isActive && (
                              <div>
                                <div className="flex items-center justify-between text-xs mb-1">
                                  <span className="text-gray-500">Submitted</span>
                                  <span className="text-gray-900">
                                    {entry.submitted_count}/{totalEnrolled}
                                  </span>
                                </div>
                                <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-green-500 rounded-full transition-all"
                                    style={{
                                      width: `${totalEnrolled > 0 ? (entry.submitted_count / totalEnrolled) * 100 : 0}%`,
                                    }}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// Main Dashboard Page
export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [autoAssignWindow, setAutoAssignWindow] = useState<WindowInfo | null>(null);

  // Cohort comes from the shared admin scope (topbar switcher); the dashboard
  // needs a concrete one, so "All cohorts" resolves to the default.
  const { programmeId, defaultProgrammeId } = useAdminScope();

  const programmesQuery = useAdminProgrammes();
  const programmes: ProgrammeItem[] = programmesQuery.data ?? [];

  const selectedProgrammeId = programmeId ?? defaultProgrammeId;

  // Changing `selectedProgrammeId` swaps the query key, so React Query refetches
  // the dashboard for the newly selected programme on its own.
  const dashboardQuery = useAdminDashboard(selectedProgrammeId);
  const dashboard: DashboardData | null = dashboardQuery.data ?? null;

  const handleAutoAssignSuccess = () => {
    dashboardQuery.refetch();
  };

  if (programmesQuery.isPending) {
    return (
      <AdminLayout>
        <LoadingState className="h-full" />
      </AdminLayout>
    );
  }

  if (programmesQuery.isError) {
    return (
      <AdminLayout>
        <ErrorState
          message={
            programmesQuery.error instanceof Error
              ? programmesQuery.error.message
              : "Failed to load programmes"
          }
          onRetry={() => programmesQuery.refetch()}
        />
      </AdminLayout>
    );
  }

  if (programmes.length === 0) {
    return (
      <AdminLayout>
        <div className="mx-auto max-w-md">
          <Card>
            <CardContent className="p-12 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <BookOpen className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No programmes yet</h3>
              <p className="text-gray-500 mb-6">Create a programme to see the dashboard</p>
              <button
                onClick={() => navigate("/admin/programmes")}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white rounded-lg transition-colors"
              >
                Go to Programmes
              </button>
            </CardContent>
          </Card>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        {/* Cohort selection lives in the admin top bar (shared scope switcher) */}
        {dashboardQuery.isPending ? (
          <LoadingState className="py-24" />
        ) : dashboardQuery.isError ? (
          <ErrorState
            className="py-24"
            message={
              dashboardQuery.error instanceof Error
                ? dashboardQuery.error.message
                : "Failed to load dashboard"
            }
            onRetry={() => dashboardQuery.refetch()}
          />
        ) : dashboard ? (
          <div className="space-y-6">
            {/* Programme Status */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-gray-500 text-sm mb-1">Programme Status</div>
                    <div className="text-2xl font-bold text-gray-900">
                      Week {dashboard.programme.current_week} of {dashboard.programme.total_weeks}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-2 text-gray-900">
                      <Users className="w-5 h-5" />
                      <span className="text-xl font-semibold">
                        {dashboard.programme.enrolled_users}
                      </span>
                      <span className="text-gray-500">enrolled</span>
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      {format(new Date(dashboard.programme.start_date), "MMM d")} -{" "}
                      {format(new Date(dashboard.programme.end_date), "MMM d, yyyy")}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Main Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Topic Inventory */}
              <TopicInventoryCard
                inventory={dashboard.topic_inventory}
                onManageTopics={() => navigate("/admin/topics")}
                onGenerateTopics={() => navigate("/admin/topics/generate")}
              />

              {/* Current Window - status only, no auto-assign */}
              <WindowCard
                window={dashboard.current_window}
                label="Current Window"
                isCurrent={true}
              />

              {/* Next Window - can auto-assign */}
              <WindowCard
                window={dashboard.next_window}
                label="Next Window"
                isCurrent={false}
                onAutoAssign={
                  dashboard.next_window?.users_need_topics
                    ? () => setAutoAssignWindow(dashboard.next_window)
                    : undefined
                }
              />
            </div>

            {/* Timeline */}
            <WeekTimeline
              timeline={dashboard.timeline}
              totalEnrolled={dashboard.programme.enrolled_users}
            />

            {/* Link to Assignments */}
            <div className="flex justify-end">
              <Link
                to="/admin/assignments"
                className="flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors"
              >
                <FileText className="w-4 h-4" />
                <span>Manage All Assignments</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        ) : (
          <div className="text-center py-12 text-gray-400">
            Select a programme to view the dashboard
          </div>
        )}
      </div>

      {/* Auto-Assign Modal */}
      {autoAssignWindow && (
        <AutoAssignModal
          open={true}
          onClose={() => setAutoAssignWindow(null)}
          assignmentId={autoAssignWindow.assignment_id}
          weekNumber={autoAssignWindow.week_number}
          assignmentNumber={autoAssignWindow.assignment_number}
          onSuccess={handleAutoAssignSuccess}
        />
      )}
    </AdminLayout>
  );
}
